import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { UserModelRequest } from 'src/app/_models/User/UserModel';
import { ApiService } from 'src/app/_services/api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from 'src/app/_services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CompressImageService } from 'src/app/_services/compress-image.service';
import { CompanyService } from 'src/app/company.service';
import { CompanyModel } from 'src/app/_models/Company/CompanyModel';

@Component({
  selector: 'app-personal-profile',
  templateUrl: './personal-profile.component.html',
  styleUrls: ['./personal-profile.component.scss']
})
export class PersonalProfileComponent implements OnInit, OnDestroy {

  private userSub?: Subscription;
  imagePath: string | undefined;
  personalProfileForm: UntypedFormGroup;
  firstName: string | undefined;
  lastName: string | undefined;
  phone: string | undefined;
  email: string | undefined;
  dateOfBirth: string | undefined;
  companyName: string | undefined;
  enableEdit: boolean = false
  loading: boolean = false
  maxSize: number = 3000000;  //3 MB = 3000000 Bytes (in decimal)
  pictureFormat: Array<string>;
  // pictureIsToBig : boolean = false;
  pictureIsWrongFormat: boolean = false;
  // showEditMode : boolean = false;
  isAdvertiser: boolean = false;
  maxDate: any;

  constructor(
    private openFile: ElementRef,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private compressImage: CompressImageService,
    private companyService: CompanyService
  ) {
    authService.hideNavigationMenuEventEmitter.emit(false);
    if(this.router.getCurrentNavigation()?.extras?.state?.editProfile){
      this.getUser();
      this.setPersonalProfileForm();
      this.showEdit();
    }
    authService.showEditProfile.subscribe(response => {
      if (response) {
        this.getUser();
        this.setPersonalProfileForm();
        this.showEdit();
      }
    });
    this.pictureFormat = ['image/png', 'image/jpg', 'image/jpeg'];
    this.maxDate = new Date();
  }

  ngOnInit(): void {
    this.getUser();
    this.setPersonalProfileForm();
  }

  getUser(): void {
    this.loading = true;
    this.userSub = this.authService.user.subscribe(user => {
      this.firstName = user?.user?.firstName;
      this.lastName = user?.user?.lastName;
      this.phone = user?.user?.phone;
      if (user?.user?.profilePictureUrl != null) {
        this.imagePath = user?.user?.profilePictureUrl;
      }
      else {
        this.imagePath = './assets/images/icons/profile-picture-placeholder.png';
      }
      this.dateOfBirth = user?.user?.dateOfBirth;
      const datePipe: DatePipe = new DatePipe('en-US');
      let formattedDate = datePipe.transform(this.dateOfBirth, 'yyyy-MM-dd');
      if (formattedDate !== null) {
        this.dateOfBirth = formattedDate;
      }
      this.email = user?.user?.name;
      // this.companyName = user?.companyName;
      this.initPersonalProfileForm();
      this.loading = false;
    });
    this.authService._isAdvertiser$.subscribe((result)=>{
      this.isAdvertiser = result;
    })
    this.getCompanyDetails();
  }

  getCompanyDetails(): void {
    this.apiService.getCompanyDetails().subscribe((company: CompanyModel) => {
      this.companyName = company.companyName;
    });
  }

  initPersonalProfileForm(): void {
    this.personalProfileForm = new UntypedFormGroup({
      profileImagePath: new UntypedFormControl({ value: this.imagePath}),
      firstName: new UntypedFormControl({ value: this.firstName, disabled: true },[Validators.required]),
      lastName: new UntypedFormControl({ value: this.lastName, disabled: true },[Validators.required]),
      phone: new UntypedFormControl({ value: this.phone, disabled: true },[Validators.required,Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$')]),
      dateOfBirth: new UntypedFormControl({ value: '', },[Validators.required]),//todo
    });
    this.setPersonalProfileForm();
  }

  setPersonalProfileForm(){
    this.personalProfileForm.patchValue({ profileImagePath: this.imagePath });
    this.personalProfileForm.patchValue({ firstName: this.firstName });
    this.personalProfileForm.patchValue({ lastName: this.lastName });
    this.personalProfileForm.patchValue({ phone: this.phone });
    if(this.dateOfBirth){
      let date = new Date((this.dateOfBirth));
      this.personalProfileForm.patchValue({ dateOfBirth: date });
    } else {
      let date = new Date();
      this.personalProfileForm.patchValue({ dateOfBirth: date });
    }
  }

  showEdit(): void {
    this.enableEdit = true;
    this.personalProfileForm.enable();
  }

  openAddImage(): void {
    let element = this.openFile.nativeElement.querySelector(".open-file");
    element.click();
  }

  onFileSelected(event: any): void {
    this.pictureIsWrongFormat = false;
    if (event.target.files && event.target.files[0]) {
      let reader = new FileReader();
      if (!this.pictureFormat.includes(event.target.files[0].type)) {
        this.pictureIsWrongFormat = true;
        return;
      }
      let image: File = event.target.files[0];
      reader.readAsDataURL(image);// read file as data url
      this.compressImage.compress(image)
        .pipe(take(1))
        .subscribe(compressedImage => {
          this.personalProfileForm.get('profileImagePath')?.setValue(compressedImage);
        });
      reader.onload = (item) => { // called once readAsDataURL is completed
        if (item.target?.result) {
          this.imagePath = item.target?.result as string;
        }
      }
    }
  }

  cancel(): void {
    this.enableEdit = false;
    this.personalProfileForm.disable();
    this.getUser();
    this.setPersonalProfileForm();
  }

  save(): void {
    this.loading = true;
    let userModel: UserModelRequest = {};
    userModel.FirstName = this.personalProfileForm.controls['firstName'].value;
    userModel.LastName = this.personalProfileForm.controls['lastName'].value;
    userModel.Phone = this.personalProfileForm.controls['phone'].value;
    const datePipe: DatePipe = new DatePipe('en-US');
    userModel.DateOfBirth = datePipe.transform(this.personalProfileForm.controls['dateOfBirth'].value, 'MM/dd/yyyy');
    this.apiService.updateUserProfile(userModel).subscribe((data) => {
      if (typeof (this.personalProfileForm.controls['profileImagePath'].value) == 'object') {
        this.apiService.uploadProfilePicture(this.personalProfileForm.controls['profileImagePath'].value).subscribe((data) => {
          this.personalProfileForm.reset();
          this.enableEdit = false;
          this.authService.handleAuthentication();
          this.snackBar.open('Profile successfully updated!', 'Cancel', {
            duration: 3000
          });
          this.loading = false;
          this.personalProfileForm.disable();
        })
      } else {
        this.personalProfileForm.reset();
        this.enableEdit = false;
        this.authService.handleAuthentication();
        this.snackBar.open('Profile successfully updated!', 'Cancel', {
          duration: 3000
        });
        this.loading = false;
        this.personalProfileForm.disable();
      }
    });
  }

  onFileInputClick(event: any): void {
    event.target.value = null;
  }

  ngOnDestroy(): void {
    if (this.userSub !== undefined) {
      this.userSub.unsubscribe();
    }
  }
}
